import React from 'react';
import { Text, Switch, Select } from '@chatsdet/ui';
import { useSeedWorkflowOutputs } from '../../../hooks/useSeedWorkflowOutputs';
import {
  CredentialPersonaPicker,
  useCredentialAndPersonaOptions,
} from './CredentialPersonaPicker';
import type { WorkflowInputDefinition } from '@chatsdet/types';

type LoginNodeConfigProps = {
  isActive: boolean;
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  upstreamSeedWorkflowId?: string | null;
  workflowInputs?: WorkflowInputDefinition[];
};

export const LoginNodeConfig: React.FC<LoginNodeConfigProps> = ({
  isActive: _isActive,
  localData,
  setLocalData,
  upstreamSeedWorkflowId,
  workflowInputs,
}) => {
  const credentialSource = localData.credentialSource ?? 'static';
  const seedRef = localData.seedCredentialRef as
    | { seedWorkflowId?: string; dataKey?: string; name?: string }
    | undefined;

  // Credential + persona options (shared with the api_sequence step editor).
  // The login node lists org-wide, so no property filter.
  const credentialOptions = useCredentialAndPersonaOptions();
  const { templateIdSet } = credentialOptions;
  const credentialInputs = React.useMemo(
    () => (workflowInputs ?? []).filter((input) => input.kind === 'credential'),
    [workflowInputs]
  );

  // Build credential source options dynamically — only show "From seed script" when there's an upstream seed
  const credentialSourceOptions = React.useMemo(() => {
    const options: { id: string; label: string }[] = [{ id: 'static', label: 'Static credential' }];
    if (credentialInputs.length > 0) {
      options.push({ id: 'from_input', label: 'From scenario input' });
    }
    if (upstreamSeedWorkflowId) {
      options.push({ id: 'from_seed', label: 'From seed script' });
    }
    return options;
  }, [credentialInputs.length, upstreamSeedWorkflowId]);

  // Load seed outputs for the upstream seed workflow
  const { data: seedOutputsData } = useSeedWorkflowOutputs(
    upstreamSeedWorkflowId ?? undefined
  );
  const seedOutputs = seedOutputsData?.outputs ?? [];
  const allCredentialOutputs = seedOutputs.filter((o) => o.credentialOutput);
  const credentialOutputs = allCredentialOutputs.filter((o) => o.dataKey);

  // Auto-select when there's exactly one valid credential output
  React.useEffect(() => {
    if (credentialSource !== 'from_seed') return;
    if (credentialOutputs.length !== 1) return;
    const onlyOutput = credentialOutputs[0];
    if (seedRef?.dataKey === onlyOutput.dataKey) return;
    setLocalData((prev) => ({
      ...prev,
      seedCredentialRef: {
        seedWorkflowId: upstreamSeedWorkflowId,
        dataKey: onlyOutput.dataKey,
        name: onlyOutput.name || onlyOutput.dataKey,
      },
    }));
  }, [credentialSource, credentialOutputs, seedRef?.dataKey, upstreamSeedWorkflowId, setLocalData]);

  // Keep seedCredentialRef.seedWorkflowId in sync with the upstream seed node
  React.useEffect(() => {
    if (credentialSource !== 'from_seed') return;

    if (!upstreamSeedWorkflowId || (seedOutputsData && credentialOutputs.length === 0)) {
      // Upstream seed disappeared or has no credential outputs — reset to static
      setLocalData((prev) => {
        const { seedCredentialRef: _removed, ...rest } = prev;
        return { ...rest, credentialSource: 'static' };
      });
      return;
    }

    // Auto-sync seedWorkflowId to the detected upstream value
    setLocalData((prev) => {
      const prevRef = prev.seedCredentialRef as
        | { seedWorkflowId?: string; dataKey?: string }
        | undefined;
      if (prevRef?.seedWorkflowId === upstreamSeedWorkflowId) return prev;
      return {
        ...prev,
        seedCredentialRef: {
          seedWorkflowId: upstreamSeedWorkflowId,
          dataKey: '',
        },
      };
    });
  }, [credentialSource, upstreamSeedWorkflowId, seedOutputsData, credentialOutputs.length, setLocalData]);

  return (
    <div className="space-y-4">
      <Text className="text-sm text-muted-foreground">
        Select which credential this Acting As step should use. Credentials are applied when the
        workflow starts running.
      </Text>

      {(upstreamSeedWorkflowId && credentialOutputs.length > 0) || credentialInputs.length > 0 ? (
        <Select
          label="Credential Source"
          selectedKey={credentialSource}
          onSelectionChange={(key) => {
            if (key === 'static') {
              setLocalData({
                ...localData,
                credentialSource: 'static',
                seedCredentialRef: null,
                credentialInputName: null,
              });
            } else if (key === 'from_input') {
              setLocalData({
                ...localData,
                credentialSource: 'from_input',
                credentialInputName: credentialInputs[0]?.name ?? '',
                credentialId: null,
                credentialTemplateId: null,
                credentialName: null,
                seedCredentialRef: null,
              });
            } else {
              setLocalData({
                ...localData,
                credentialSource: 'from_seed',
                credentialId: null,
                credentialTemplateId: null,
                credentialName: null,
                credentialInputName: null,
                seedCredentialRef: { seedWorkflowId: upstreamSeedWorkflowId, dataKey: '' },
              });
            }
          }}
          items={credentialSourceOptions}
        />
      ) : null}

      {credentialSource === 'from_input' && (
        <Select
          label="Scenario credential input"
          selectedKey={localData.credentialInputName ?? null}
          placeholder="Select workflow credential input..."
          items={credentialInputs.map((input) => ({ id: input.name, label: input.label }))}
          onSelectionChange={(key) =>
            setLocalData({
              ...localData,
              credentialSource: 'from_input',
              credentialInputName: key as string,
            })
          }
        />
      )}

      {credentialSource !== 'from_seed' && credentialSource !== 'from_input' && (
        <CredentialPersonaPicker
          templates={credentialOptions.templates}
          credentials={credentialOptions.credentials}
          propertyNameById={credentialOptions.propertyNameById}
          templateNameById={credentialOptions.templateNameById}
          isLoading={credentialOptions.isLoading}
          selectedTemplateId={localData.credentialTemplateId ?? null}
          selectedCredentialId={localData.credentialId ?? null}
          onPick={(id) => {
            if (id === null) {
              setLocalData({
                ...localData,
                credentialSource: 'static',
                credentialId: null,
                credentialTemplateId: null,
                credentialName: null,
                seedCredentialRef: null,
              });
              return;
            }
            if (templateIdSet.has(id)) {
              setLocalData({
                ...localData,
                credentialSource: 'from_template',
                credentialTemplateId: id,
                credentialId: null,
                credentialName: null,
                seedCredentialRef: null,
              });
            } else {
              setLocalData({
                ...localData,
                credentialSource: 'static',
                credentialId: id,
                credentialTemplateId: null,
                credentialName: null,
                seedCredentialRef: null,
              });
            }
          }}
        />
      )}

      {credentialSource === 'from_seed' && (
        <div className="space-y-3">
          {credentialOutputs.length > 0 && (
            <Select
              label="Credential"
              selectedKey={seedRef?.dataKey ?? ''}
              onSelectionChange={(key) => {
                const selected = credentialOutputs.find((o) => o.dataKey === key);
                setLocalData({
                  ...localData,
                  seedCredentialRef: {
                    seedWorkflowId: upstreamSeedWorkflowId,
                    dataKey: key as string,
                    name: selected?.name || (key as string),
                  },
                });
              }}
              items={credentialOutputs.map((o) => ({
                id: o.dataKey,
                label: `${o.name} (${o.dataKey})`,
              }))}
              placeholder="Select a credential output..."
            />
          )}

          {credentialOutputs.length === 0 && allCredentialOutputs.length > 0 && (
            <Text className="text-xs text-amber-500">
              The upstream seed script has credential outputs with missing data keys. Configure them
              in the seed script settings.
            </Text>
          )}

          {credentialOutputs.length === 0 &&
            allCredentialOutputs.length === 0 &&
            seedOutputs.length > 0 && (
              <Text className="text-xs text-amber-500">
                This seed workflow has no outputs with credential provisioning configured. Enable
                "Provisions a credential" on a seed output first.
              </Text>
            )}
        </div>
      )}

      <div className="space-y-1.5">
        <Switch
          size="sm"
          isSelected={localData.performLogin ?? true}
          onChange={(isSelected) => {
            setLocalData({
              ...localData,
              performLogin: isSelected,
            });
          }}
          label="Logged in"
        />
        <Text className="text-xs text-muted-foreground">
          Defaults on, which is probably what you want. When turned on, the agent will repurpose
          existing logged-in sessions or log in again for you if the session has expired. Turn off
          to test logged-out flows such as password reset or to test the login flow directly.
        </Text>
      </div>
    </div>
  );
};
